import { useState, type FormEvent } from "react";
import { siteInfo } from "@/mocks/content";

const FORM_URL = "https://readdy.ai/api/form/db6e047qhq9pk5mecstg";
const MAX_MESSAGE_LENGTH = 500;

type SubmitStatus = "idle" | "loading" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = String(formData.get("company_alt") || "").trim();
    if (honeypotValue) {
      setStatus("success");
      return;
    }
    formData.delete("company_alt");

    setStatus("loading");
    setErrorMessage("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, String(value));
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试。";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
        setMessageLength(0);
      } else {
        setStatus("error");
        setErrorMessage(serverMessage);
      }
    } catch {
      setStatus("error");
      setErrorMessage("网络异常，请检查网络连接后重试。");
    }
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
      <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
        在线留言
      </h3>
      <p className="mt-2 text-sm text-foreground-500">
        填写下方信息，我们会尽快与您取得联系。（演示表单，提交将被记录为线索数据）
      </p>

      <form
        id="contact-message-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="mt-6 flex flex-col gap-5"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
              姓名
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              required
              maxLength={60}
              placeholder="请输入您的姓名"
              className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
              邮箱
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              required
              placeholder="请输入您的邮箱"
              className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
        </div>

        <div>
          <label htmlFor="contact-phone" className="block text-sm text-foreground-700 mb-1.5">
            联系电话（选填）
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            maxLength={20}
            placeholder="请输入您的联系电话"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1.5">
            咨询类型
          </label>
          <select
            id="contact-subject"
            name="subject"
            defaultValue="品牌合作"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer transition-colors"
          >
            <option value="品牌合作">品牌合作</option>
            <option value="产品咨询">产品咨询</option>
            <option value="媒体采访">媒体采访</option>
            <option value="其他">其他</option>
          </select>
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
            留言内容
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            rows={5}
            maxLength={MAX_MESSAGE_LENGTH}
            onChange={(e) => setMessageLength(e.target.value.length)}
            placeholder="请输入您想咨询或分享的内容（最多 500 字）"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
          ></textarea>
          <p className="mt-1 text-right text-xs text-foreground-400">
            {messageLength}/{MAX_MESSAGE_LENGTH}
          </p>
        </div>

        <input
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="form-field__assist"
        />

        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              提交中…
            </>
          ) : (
            <>
              提交留言
              <i className="ri-send-plane-line ml-2"></i>
            </>
          )}
        </button>

        {status === "success" && (
          <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3 flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5"></i>
            留言提交成功，感谢您的关注，我们会尽快与您联系！
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-start gap-2">
            <i className="ri-error-warning-line mt-0.5"></i>
            {errorMessage}
          </p>
        )}
      </form>

      <p className="mt-4 text-xs text-foreground-400">
        您也可以直接通过电话或邮箱联系我们：{siteInfo.phone} / {siteInfo.email}
      </p>
    </div>
  );
}